import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { AcceptanceCriterionDraft } from '../../../models/save-full-requirement.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { ScrollContainerDirective } from '../../../shared/scroll-container/scroll-container.directive';

@Component({
  selector: 'app-acceptance-criteria-table',
  imports: [TranslatePipe, MatIconButton, MatIcon, MatCheckbox, ScrollContainerDirective],
  templateUrl: './acceptance-criteria-table.component.html',
  styleUrl: './acceptance-criteria-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AcceptanceCriteriaTableComponent {
  readonly criteria = input.required<AcceptanceCriterionDraft[]>();
  readonly readOnly = input(false);
  // Jira-verknuepft: Text/Menge/Reihenfolge kommen aus Jira und sind gesperrt,
  // das Abhaken (checked) bleibt aber als natives Fortschritts-Tracking moeglich.
  readonly contentLocked = input(false);
  readonly editingDisabled = computed(() => this.readOnly() || this.contentLocked());
  readonly changed = output<AcceptanceCriterionDraft[]>();
  readonly fieldLengths = FIELD_LENGTHS;

  readonly checkedCount = computed(() => this.criteria().filter((c) => c.checked).length);
  readonly newText = signal('');
  readonly editingKey = signal<string | null>(null);
  readonly editText = signal('');

  trackKey(item: AcceptanceCriterionDraft): string {
    return item.id ?? item.clientId ?? '';
  }

  onNewTextInput(event: Event): void {
    this.newText.set((event.target as HTMLInputElement).value);
  }

  onEditTextInput(event: Event): void {
    this.editText.set((event.target as HTMLInputElement).value);
  }

  add(): void {
    if (this.editingDisabled()) {
      return;
    }
    const text = this.newText().trim();
    if (!text) {
      return;
    }
    const draft: AcceptanceCriterionDraft = {
      clientId: this.generateClientId(),
      text,
      checked: false,
    };
    this.changed.emit([...this.criteria(), draft]);
    this.newText.set('');
  }

  startEdit(criterion: AcceptanceCriterionDraft): void {
    if (this.editingDisabled()) {
      return;
    }
    this.editingKey.set(this.trackKey(criterion));
    this.editText.set(criterion.text);
  }

  cancelEdit(): void {
    this.editingKey.set(null);
    this.editText.set('');
  }

  saveEdit(): void {
    const key = this.editingKey();
    const text = this.editText().trim();
    if (!key || !text) {
      return;
    }
    this.changed.emit(this.criteria().map((c) => (this.trackKey(c) === key ? { ...c, text } : c)));
    this.cancelEdit();
  }

  toggleChecked(criterion: AcceptanceCriterionDraft): void {
    if (this.readOnly()) {
      return;
    }
    const key = this.trackKey(criterion);
    this.changed.emit(this.criteria().map((c) => (this.trackKey(c) === key ? { ...c, checked: !c.checked } : c)));
  }

  delete(criterion: AcceptanceCriterionDraft): void {
    if (this.editingDisabled()) {
      return;
    }
    const key = this.trackKey(criterion);
    this.changed.emit(this.criteria().filter((c) => this.trackKey(c) !== key));
  }

  moveUp(index: number): void {
    if (this.editingDisabled()) {
      return;
    }
    if (index > 0) {
      this.swap(index, index - 1);
    }
  }

  moveDown(index: number): void {
    if (this.editingDisabled()) {
      return;
    }
    if (index < this.criteria().length - 1) {
      this.swap(index, index + 1);
    }
  }

  private swap(from: number, to: number): void {
    const next = [...this.criteria()];
    [next[from], next[to]] = [next[to], next[from]];
    this.changed.emit(next);
  }

  private generateClientId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `ac-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
